/**
 * Применяет НЕ-цветовые UI токены из design-config.ts к CSS переменным
 * Цвета теперь применяются через новую систему applyTheme
 *
 * Вызывается при старте приложения для обратной совместимости
 */

import { designConfig } from './design-config';
import { uiTokens } from '../design/ui-tokens';
import { v2Tokens, v2StatusPillBg, v2StatusPillText } from '../design/v2-tokens';

function v2CssKey(segment: string): string {
  return segment.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

function resolvedDataThemeAttr(): string | null {
  return typeof document !== 'undefined' ? document.documentElement.getAttribute('data-theme') : null;
}

const DARK_TINTED_CHIP_PROPS = [
  '--dark-tinted-chip-bg-a',
  '--dark-tinted-chip-bg-b',
  '--dark-tinted-chip-border-a',
  '--dark-tinted-chip-border-b',
  '--dark-tinted-chip-fg-a',
  '--dark-tinted-chip-fg-b',
  '--dark-colored-add-bg-a',
  '--dark-colored-add-bg-b',
  '--dark-colored-add-border-a',
  '--dark-colored-add-border-b',
  '--dark-add-tone-appointment',
  '--dark-add-tone-client',
  '--dark-add-tone-category',
  '--dark-add-tone-service',
  '--dark-add-tone-product',
] as const;

export function applyDesignConfig() {
  const root = document.documentElement;
  const isDark = resolvedDataThemeAttr() === 'dark';
  /** Светлая: mix пастели с card; +10 п.п. базы — чуть насыщеннее. Тёмная: переменные в if(isDark). */
  const ADD_BTN_BG_PCT = isDark ? 22 : 67;
  const ADD_BTN_BORDER_PCT = isDark ? 34 : 66;
  const mixWithCard = (base: string, basePct = ADD_BTN_BG_PCT) =>
    `color-mix(in srgb, ${base} ${basePct}%, var(--c-card) ${100 - basePct}%)`;
  const mixWithText = (base: string, basePct = isDark ? 78 : 82) =>
    `color-mix(in srgb, ${base} ${basePct}%, var(--c-text) ${100 - basePct}%)`;
  if (isDark) {
    root.style.setProperty('--dark-tinted-chip-bg-a', '22%');
    root.style.setProperty('--dark-tinted-chip-bg-b', '78%');
    root.style.setProperty('--dark-tinted-chip-border-a', '34%');
    root.style.setProperty('--dark-tinted-chip-border-b', '66%');
    /** Иконка/подпись: primary чуть приглушаем чёрным (не через c-text — иначе «белеет») */
    root.style.setProperty('--dark-tinted-chip-fg-a', '74%');
    root.style.setProperty('--dark-tinted-chip-fg-b', '26%');
    /** Цветные «+» в топбаре: насыщеннее чипа таба (+10 п.п. к доле акцента) */
    root.style.setProperty('--dark-colored-add-bg-a', '32%');
    root.style.setProperty('--dark-colored-add-bg-b', '68%');
    root.style.setProperty('--dark-colored-add-border-a', '44%');
    root.style.setProperty('--dark-colored-add-border-b', '56%');
    root.style.setProperty('--dark-add-tone-appointment', 'var(--c-info)');
    root.style.setProperty('--dark-add-tone-client', 'var(--c-success)');
    root.style.setProperty('--dark-add-tone-category', designConfig.uiAccents.tones.purple.fg);
    root.style.setProperty('--dark-add-tone-service', designConfig.uiAccents.tones.pink.fg);
    root.style.setProperty('--dark-add-tone-product', designConfig.uiAccents.tones.teal.fg);
  } else {
    DARK_TINTED_CHIP_PROPS.forEach((p) => root.style.removeProperty(p));
  }

  // ============================================
  // 🎨 ЦВЕТА ТЕПЕРЬ ПРИМЕНЯЮТСЯ ЧЕРЕЗ applyTheme!
  // ============================================
  // Все цветовые CSS переменные (--color-*, --c-*) теперь в src/shared/design/colors/applyTheme.ts
  
  // ============================================
  // 📏 UI ТОКЕНЫ (spacing, radius, etc.)
  // ============================================

  // Spacing (из design-config)
  Object.entries(designConfig.spacing).forEach(([key, value]) => {
    root.style.setProperty(`--spacing-${key}`, value);
  });
  
  // Spacing алиасы для удобства (единый источник правды)
  root.style.setProperty('--spacing-xs', designConfig.spacing[4]);   // 4px
  root.style.setProperty('--spacing-sm', designConfig.spacing[8]);   // 8px
  root.style.setProperty('--spacing-md', designConfig.spacing[12]);  // 12px
  root.style.setProperty('--spacing-lg', designConfig.spacing[16]); // 16px
  root.style.setProperty('--spacing-xl', designConfig.spacing[24]); // 24px

  // Radius (из design-config)
  Object.entries(designConfig.radius).forEach(([key, value]) => {
    root.style.setProperty(`--radius-${key}`, value);
  });

  // Elevation (из design-config)
  Object.entries(designConfig.elevation).forEach(([key, value]) => {
    root.style.setProperty(`--elevation-${key}`, value);
  });

  // Z-Index (из design-config)
  Object.entries(designConfig.zIndex).forEach(([key, value]) => {
    root.style.setProperty(`--z-index-${key}`, value.toString());
  });

  // Modal (из design-config)
  root.style.setProperty('--modal-top-offset', designConfig.modal.topOffset);
  root.style.setProperty('--modal-side-margin', designConfig.modal.sideMargin);
  root.style.setProperty('--modal-bottom-margin', designConfig.modal.bottomMargin);
  root.style.setProperty('--modal-max-width', designConfig.modal.maxWidth);

  // Effects (из design-config)
  root.style.setProperty('--effect-modal-backdrop-filter', designConfig.effects.modalBackdropFilter);
  root.style.setProperty('--effect-overlay-backdrop-filter', designConfig.effects.overlayBackdropFilter);
  // Алиас для modal (использует effects.modalBackdropFilter - единый источник правды)
  root.style.setProperty('--modal-backdrop-filter', designConfig.effects.modalBackdropFilter);

  // List (из design-config)
  root.style.setProperty('--list-item-padding', designConfig.spacing[12]); // Единый источник правды

  // SegmentedToggle размеры (цвета теперь через CSS vars из тем)
  root.style.setProperty('--segmented-height-sm', designConfig.segmented.height.sm);
  root.style.setProperty('--segmented-height-md', designConfig.segmented.height.md);
  root.style.setProperty('--segmented-height-lg', designConfig.segmented.height.lg);
  root.style.setProperty('--segmented-pad-x-sm', designConfig.segmented.paddingX.sm);
  root.style.setProperty('--segmented-pad-x-md', designConfig.segmented.paddingX.md);
  root.style.setProperty('--segmented-pad-x-lg', designConfig.segmented.paddingX.lg);
  root.style.setProperty('--segmented-radius-sm', designConfig.segmented.radius.sm);
  root.style.setProperty('--segmented-radius-md', designConfig.segmented.radius.md);
  root.style.setProperty('--segmented-radius-lg', designConfig.segmented.radius.lg);
  root.style.setProperty('--segmented-gap', designConfig.segmented.gap);

  // Control размеры (из design-config)
  root.style.setProperty('--control-form-height', designConfig.control.formHeight);
  root.style.setProperty('--control-compact-height', designConfig.control.compactHeight);
  root.style.setProperty('--control-touch-target-min', designConfig.control.touchTargetMin);

  // Dimensions (из ui-tokens — единый источник правды)
  root.style.setProperty('--dimensions-quantity-input-width', uiTokens.dimensions.quantityInputWidth);
  
  // Select размеры
  root.style.setProperty('--select-height-sm', designConfig.control.select.sm);
  root.style.setProperty('--select-height-md', designConfig.control.select.md);
  
  // Button размеры
  root.style.setProperty('--button-height-sm', designConfig.control.button.sm.height);
  root.style.setProperty('--button-padding-x-sm', designConfig.control.button.sm.paddingX);
  root.style.setProperty('--button-padding-y-sm', designConfig.control.button.sm.paddingY);
  root.style.setProperty('--button-height-md', designConfig.control.button.md.height);
  root.style.setProperty('--button-padding-x-md', designConfig.control.button.md.paddingX);
  root.style.setProperty('--button-padding-y-md', designConfig.control.button.md.paddingY);
  
  // IconButton размеры
  root.style.setProperty('--icon-button-size-sm', designConfig.control.iconButton.sm.size);
  root.style.setProperty('--icon-button-size-md', designConfig.control.iconButton.md.size);
  root.style.setProperty('--icon-button-size-lg', designConfig.control.iconButton.lg.size);
  
  // Search размеры
  root.style.setProperty('--search-height', designConfig.control.search.height);
  root.style.setProperty('--search-padding-x', designConfig.control.search.paddingX);
  root.style.setProperty('--search-padding-x-with-icon', designConfig.control.search.paddingXWithIcon);
  root.style.setProperty('--search-icon-offset', designConfig.control.search.iconOffset);

  // Layout (из design-config)
  root.style.setProperty('--layout-header-height', designConfig.layout.headerHeight);
  root.style.setProperty('--layout-tabs-height', designConfig.layout.tabsHeight);
  root.style.setProperty('--layout-content-bottom-padding', designConfig.layout.contentBottomPadding);
  root.style.setProperty('--layout-max-width', designConfig.layout.maxWidth);

  // Screen (из design-config) - цвета теперь через CSS vars
  root.style.setProperty('--screen-max-width', designConfig.screen.maxWidth);
  root.style.setProperty('--screen-side-padding', designConfig.screen.sidePadding);
  root.style.setProperty('--screen-top-offset', designConfig.screen.topOffset);
  root.style.setProperty('--screen-bottom-offset', designConfig.screen.bottomOffset);
  root.style.setProperty('--screen-radius', designConfig.screen.radius);
  root.style.setProperty('--screen-border', designConfig.screen.border);

  // Typography (из design-config)
  root.style.setProperty('--font-family-base', designConfig.typography.fontFamily);
  root.style.setProperty('--font-sans', designConfig.typography.fontFamily); // Алиас для единообразия
  
  // Typography font sizes (роли: title=md, body=sm, meta=xs, micro)
  root.style.setProperty('--typography-font-size-micro', designConfig.typography.fontSize.micro);
  root.style.setProperty('--typography-font-size-xs', designConfig.typography.fontSize.xs);
  root.style.setProperty('--typography-font-size-sm', designConfig.typography.fontSize.sm);
  root.style.setProperty('--typography-font-size-md', designConfig.typography.fontSize.md);
  root.style.setProperty('--typography-font-size-lg', designConfig.typography.fontSize.lg);
  root.style.setProperty('--typography-font-size-xl', designConfig.typography.fontSize.xl);
  root.style.setProperty('--font-size-title', designConfig.typography.fontSize.md);
  root.style.setProperty('--font-size-body', designConfig.typography.fontSize.sm);
  root.style.setProperty('--font-size-meta', designConfig.typography.fontSize.xs);
  root.style.setProperty('--font-size-micro', designConfig.typography.fontSize.micro);

  // Font weights (роли UI-KIT)
  root.style.setProperty('--font-weight-regular', String(designConfig.typography.fontWeight.regular));
  root.style.setProperty('--font-weight-medium', String(designConfig.typography.fontWeight.medium));
  root.style.setProperty('--font-weight-semibold', String(designConfig.typography.fontWeight.semibold));
  root.style.setProperty('--font-weight-bold', String(designConfig.typography.fontWeight.bold));

  // ============================================
  // 📋 LEGACY СОВМЕСТИМОСТЬ (старые переменные для CSS)
  // ============================================
  // Старые алиасы для обратной совместимости - CSS все еще использует старые имена
  root.style.setProperty('--color-bg', 'var(--c-bg)'); // Основной фон
  root.style.setProperty('--color-surface', 'var(--c-surface)'); // Поверхность контента
  root.style.setProperty('--color-card', 'var(--c-card)'); // Карточки и модалки
  root.style.setProperty('--color-elevated', 'var(--c-elevated)'); // Приподнятые поверхности
  root.style.setProperty('--color-border', 'var(--c-border)'); // Границы
  root.style.setProperty('--color-border-strong', 'var(--c-border-strong)'); // Сильные границы
  root.style.setProperty('--color-text', 'var(--c-text)'); // Основной текст
  root.style.setProperty('--color-text-muted', 'var(--c-text-muted)'); // Приглушенный текст
  root.style.setProperty('--color-text-secondary', 'var(--c-text-muted)'); // Алиас
  root.style.setProperty('--color-scrollbar-track', 'var(--c-scrollbar-track)'); // Трек скроллбара (график и др.)
  root.style.setProperty('--color-scrollbar-thumb', 'var(--c-scrollbar-thumb)'); // Ползунок
  root.style.setProperty('--color-scrollbar-thumb-hover', 'var(--c-scrollbar-thumb-hover)'); // Ползунок при наведении
  root.style.setProperty('--color-text-tertiary', 'var(--c-text-tertiary)'); // Третичный текст
  root.style.setProperty('--color-text-placeholder', 'var(--c-text-placeholder)'); // Placeholder текст
  root.style.setProperty('--color-text-disabled', 'var(--c-disabled)'); // Отключенный текст

  // Primary цвета - теперь через CSS переменные из тем
  root.style.setProperty('--color-primary', 'var(--c-primary)');
  root.style.setProperty('--primary-fill', 'var(--c-primary)'); // Алиас для кнопок
  root.style.setProperty('--primary-outline', 'var(--c-primary)'); // Теперь через тему
  root.style.setProperty('--color-primary-light', 'var(--c-primary-light)'); // Теперь через тему
  root.style.setProperty('--color-primary-dark', 'var(--c-on-primary-container)'); // Теперь через тему
  root.style.setProperty('--color-primary-alt', 'var(--c-primary)'); // Теперь через тему
  root.style.setProperty('--color-on-primary', 'var(--c-on-primary)');
  root.style.setProperty('--color-on-primary-container', 'var(--c-on-primary-container)'); // Теперь через тему
  root.style.setProperty('--color-on-light-surface', 'var(--c-on-light-surface, var(--c-on-primary))'); // Темный текст на светлых плашках
  root.style.setProperty('--color-link', 'var(--c-link, var(--c-primary))'); // Ссылки/иконки на светлом фоне
  root.style.setProperty('--color-press-effect', 'rgba(from var(--c-primary) r g b / 0.35)'); // Теперь через тему
  root.style.setProperty('--primary-focus-ring', 'var(--c-focus)'); // Алиас

  // Success цвета
  root.style.setProperty('--color-success', 'var(--c-success)');
  root.style.setProperty('--color-success-light', '#D9F1E4'); // Оставляем для обратной совместимости
  root.style.setProperty('--color-success-dark', '#2F5E44'); // Оставляем для обратной совместимости
  root.style.setProperty('--color-on-success', 'var(--c-on-success)');

  // Warning цвета
  root.style.setProperty('--color-warning', 'var(--c-warning)');
  root.style.setProperty('--color-warning-light', '#F7E7CF'); // Оставляем для обратной совместимости
  root.style.setProperty('--color-warning-dark', '#7A5220'); // Оставляем для обратной совместимости
  root.style.setProperty('--color-on-warning', 'var(--c-on-warning)');

  // Error цвета
  root.style.setProperty('--color-error', 'var(--c-error)');
  root.style.setProperty('--color-danger', 'var(--c-error)'); // Алиас
  root.style.setProperty('--color-error-light', '#FCE1E1'); // Оставляем для обратной совместимости
  root.style.setProperty('--color-error-dark', '#7F2E2E'); // Оставляем для обратной совместимости
  root.style.setProperty('--color-on-error', 'var(--c-on-error)');
  root.style.setProperty('--color-on-danger', 'var(--c-on-error)'); // Алиас

  // Weekend/Выходные дни (семантически правильная переменная, визуально использует error цвет)
  root.style.setProperty('--color-weekend', 'var(--c-error)');
  root.style.setProperty('--color-weekend-overlay', 'rgba(from var(--c-error) r g b / 0.28)');

  // Info цвета
  root.style.setProperty('--color-info', 'var(--c-info)');
  root.style.setProperty('--color-info-light', '#E1EEF8'); // Оставляем для обратной совместимости
  root.style.setProperty('--color-info-dark', '#2E5877'); // Оставляем для обратной совместимости
  root.style.setProperty('--color-on-info', 'var(--c-on-info)');

  // Muted цвета
  root.style.setProperty('--color-muted', '#8B9098'); // Оставляем для обратной совместимости
  root.style.setProperty('--color-muted-light', '#EEF1F4'); // Оставляем для обратной совместимости
  root.style.setProperty('--color-bg-muted', uiTokens.disabled.background); // Цвет фона disabled полей из UI токенов - используем primary для заметного оттенка
  root.style.setProperty('--ui-disabled-text', uiTokens.disabled.text);
  root.style.setProperty('--ui-disabled-opacity', uiTokens.disabled.opacity);
  root.style.setProperty('--color-muted-dark', '#5B616A'); // Оставляем для обратной совместимости
  root.style.setProperty('--color-on-muted', '#2A2F36'); // Оставляем для обратной совместимости

  // Overlay/Scrim
  root.style.setProperty('--color-overlay', 'var(--c-scrim)');

  // Appointment цвета и стили (теперь через CSS vars)
  root.style.setProperty('--appointment-bg-body', 'var(--c-card)'); // Фон карточки записи (цвет верхнего слоя)
  root.style.setProperty('--appointment-bg-header-scheduled', 'var(--c-appt-scheduled-bg)');
  root.style.setProperty('--appointment-bg-header-completed', 'var(--c-appt-completed-bg)');
  root.style.setProperty('--appointment-bg-header-no-show', 'var(--c-appt-no-show-bg)');
  root.style.setProperty('--appointment-bg-header-cancelled', 'var(--c-appt-cancelled-bg)');
  root.style.setProperty('--appointment-bg-header-moved', 'var(--c-appt-moved-bg)');
  root.style.setProperty('--appointment-bg-header-break', 'var(--c-appt-break-bg)');
  root.style.setProperty('--status-scheduled', isDark ? 'var(--c-appt-scheduled-bg)' : 'var(--c-info)');
  root.style.setProperty('--status-completed', isDark ? 'var(--c-appt-completed-bg)' : 'var(--c-success)');
  root.style.setProperty('--status-no-show', isDark ? 'var(--c-appt-no-show-bg)' : 'var(--c-warning)');
  root.style.setProperty('--status-cancelled', isDark ? 'var(--c-appt-cancelled-bg)' : 'var(--c-error)');
  root.style.setProperty('--status-moved', isDark ? 'var(--c-appt-moved-bg)' : 'var(--c-primary)');
  root.style.setProperty('--status-break', isDark ? 'var(--c-appt-break-bg)' : 'var(--c-text-muted)');
  root.style.setProperty('--appointment-border', designConfig.appointment.border);
  root.style.setProperty('--appointment-shadow', designConfig.appointment.shadow);
  root.style.setProperty('--appointment-radius', '4px'); // Скругление углов 4px
  root.style.setProperty('--appointment-padding-x', '8px'); // Внутренний отступ 8px
  root.style.setProperty('--appointment-padding-y', '8px'); // Внутренний отступ 8px

  // Control Well
  root.style.setProperty('--control-well-bg', '#E6EAF0'); // Оставляем для обратной совместимости
  root.style.setProperty('--control-well-border', '#D5DAE3'); // Оставляем для обратной совместимости

  // Badge
  root.style.setProperty('--badge-compact-bg', 'rgba(255, 255, 255, 0.7)'); // Оставляем для обратной совместимости

  // Segmented цвета - теперь через CSS vars для поддержки тем
  root.style.setProperty('--segmented-container-bg', 'var(--color-bg)'); // Более темный фон для переключателя
  root.style.setProperty('--segmented-pill-bg', 'transparent'); // Фон неактивного сегмента
  root.style.setProperty('--segmented-pill-bg-active', 'var(--c-card)'); // Фон активного сегмента (цвет верхнего слоя)
  root.style.setProperty('--segmented-text', 'var(--c-text-muted)'); // Текст неактивного сегмента
  root.style.setProperty('--segmented-text-active', 'var(--c-text)'); // Текст активного сегмента

  // Component accents (из design-config)
  root.style.setProperty(
    '--accent-bottom-tab-active-fg',
    designConfig.uiAccents.bottomTab.activeFg
  );
  root.style.setProperty(
    '--accent-surface-blue-fg',
    mixWithText(designConfig.uiAccents.tones.blue.fg)
  );
  root.style.setProperty(
    '--accent-surface-blue-bg',
    mixWithCard(designConfig.uiAccents.tones.blue.surface, ADD_BTN_BG_PCT)
  );
  root.style.setProperty(
    '--accent-surface-blue-border',
    mixWithCard(designConfig.uiAccents.tones.blue.surface, ADD_BTN_BORDER_PCT)
  );
  root.style.setProperty(
    '--accent-surface-green-fg',
    mixWithText(designConfig.uiAccents.tones.green.fg)
  );
  root.style.setProperty(
    '--accent-surface-green-bg',
    mixWithCard(designConfig.uiAccents.tones.green.surface, ADD_BTN_BG_PCT)
  );
  root.style.setProperty(
    '--accent-surface-green-border',
    mixWithCard(designConfig.uiAccents.tones.green.surface, ADD_BTN_BORDER_PCT)
  );
  root.style.setProperty(
    '--accent-surface-yellow-fg',
    mixWithText(designConfig.uiAccents.tones.yellow.fg)
  );
  root.style.setProperty(
    '--accent-surface-yellow-bg',
    mixWithCard(designConfig.uiAccents.tones.yellow.surface, ADD_BTN_BG_PCT)
  );
  root.style.setProperty(
    '--accent-surface-yellow-border',
    mixWithCard(designConfig.uiAccents.tones.yellow.surface, ADD_BTN_BORDER_PCT)
  );
  root.style.setProperty(
    '--accent-surface-purple-fg',
    mixWithText(designConfig.uiAccents.tones.purple.fg)
  );
  root.style.setProperty(
    '--accent-surface-purple-bg',
    mixWithCard(designConfig.uiAccents.tones.purple.surface, ADD_BTN_BG_PCT)
  );
  root.style.setProperty(
    '--accent-surface-purple-border',
    mixWithCard(designConfig.uiAccents.tones.purple.surface, ADD_BTN_BORDER_PCT)
  );
  root.style.setProperty(
    '--accent-surface-pink-fg',
    mixWithText(designConfig.uiAccents.tones.pink.fg)
  );
  root.style.setProperty(
    '--accent-surface-pink-bg',
    mixWithCard(designConfig.uiAccents.tones.pink.surface, ADD_BTN_BG_PCT)
  );
  root.style.setProperty(
    '--accent-surface-pink-border',
    mixWithCard(designConfig.uiAccents.tones.pink.surface, ADD_BTN_BORDER_PCT)
  );
  root.style.setProperty(
    '--accent-surface-teal-fg',
    mixWithText(designConfig.uiAccents.tones.teal.fg)
  );
  root.style.setProperty(
    '--accent-surface-teal-bg',
    mixWithCard(designConfig.uiAccents.tones.teal.surface, ADD_BTN_BG_PCT)
  );
  root.style.setProperty(
    '--accent-surface-teal-border',
    mixWithCard(designConfig.uiAccents.tones.teal.surface, ADD_BTN_BORDER_PCT)
  );
  root.style.setProperty(
    '--accent-add-appointment-icon',
    'var(--accent-surface-blue-fg)'
  );
  root.style.setProperty(
    '--accent-add-appointment-bg',
    'var(--accent-surface-blue-bg)'
  );
  root.style.setProperty(
    '--accent-add-appointment-border',
    'var(--accent-surface-blue-border)'
  );
  root.style.setProperty(
    '--accent-add-client-icon',
    'var(--accent-surface-green-fg)'
  );
  root.style.setProperty(
    '--accent-add-client-bg',
    'var(--accent-surface-green-bg)'
  );
  root.style.setProperty(
    '--accent-add-client-border',
    'var(--accent-surface-green-border)'
  );
  root.style.setProperty(
    '--accent-add-category-icon',
    'var(--accent-surface-purple-fg)'
  );
  root.style.setProperty(
    '--accent-add-category-bg',
    'var(--accent-surface-purple-bg)'
  );
  root.style.setProperty(
    '--accent-add-category-border',
    'var(--accent-surface-purple-border)'
  );
  root.style.setProperty(
    '--accent-add-service-icon',
    'var(--accent-surface-pink-fg)'
  );
  root.style.setProperty(
    '--accent-add-service-bg',
    'var(--accent-surface-pink-bg)'
  );
  root.style.setProperty(
    '--accent-add-service-border',
    'var(--accent-surface-pink-border)'
  );
  root.style.setProperty(
    '--accent-add-product-icon',
    'var(--accent-surface-teal-fg)'
  );
  root.style.setProperty(
    '--accent-add-product-bg',
    'var(--accent-surface-teal-bg)'
  );
  root.style.setProperty(
    '--accent-add-product-border',
    'var(--accent-surface-teal-border)'
  );

  // ============================================
  // 🎨 V2 REDESIGN TOKENS (Ink) — CSS variables
  // ============================================

  const v2 = v2Tokens;
  const v2Colors = v2.colors;

  root.style.setProperty('--v2-accent', v2Colors.accent);
  root.style.setProperty('--v2-on-accent', v2Colors.onAccent);
  root.style.setProperty('--v2-ink', v2Colors.ink);
  root.style.setProperty('--v2-text-secondary', v2Colors.textSecondary);
  root.style.setProperty('--v2-text-muted', v2Colors.textMuted);
  root.style.setProperty('--v2-icon-muted', v2Colors.iconMuted);
  root.style.setProperty('--v2-border', v2Colors.border);
  root.style.setProperty('--v2-border-frame', v2Colors.borderFrame);
  root.style.setProperty('--v2-canvas', v2Colors.canvas);
  root.style.setProperty('--v2-surface', v2Colors.surface);
  root.style.setProperty('--v2-warm-tint', v2Colors.warmTint);
  root.style.setProperty('--v2-link', v2Colors.link);
  root.style.setProperty('--v2-status-done', v2Colors.status.done);
  root.style.setProperty('--v2-status-pending', v2Colors.status.pending);
  root.style.setProperty('--v2-status-neutral', v2Colors.status.neutral);

  root.style.setProperty('--v2-radius-sm', v2.radius.sm);
  root.style.setProperty('--v2-radius-md', v2.radius.md);
  root.style.setProperty('--v2-radius-sheet', v2.radius.sheet);
  root.style.setProperty('--v2-radius-frame', v2.radius.frame);
  root.style.setProperty('--v2-radius-pill', v2.radius.pill);

  root.style.setProperty('--v2-elevation-float', v2.elevation.float);
  root.style.setProperty('--v2-elevation-sheet', v2.elevation.sheet);
  root.style.setProperty('--v2-appointment-card-shadow', v2.elevation.appointmentCard);
  root.style.setProperty(
    '--v2-appointment-card-overlap-shadow',
    v2.elevation.appointmentCardOverlap
  );

  root.style.setProperty('--v2-status-stripe-width', v2.layout.statusStripeWidth);
  // Legacy stripe width intentionally unset — DayTimeline keeps pre-v2 behavior until BC-R3.

  root.style.setProperty('--v2-tab-bar-height', v2.layout.tabBarHeight);
  root.style.setProperty('--v2-tab-bar-icon-size', v2.layout.tabBarIconSize);
  root.style.setProperty('--v2-tab-bar-indicator-width', v2.layout.tabBarIndicatorWidth);
  root.style.setProperty('--v2-tab-bar-indicator-height', v2.layout.tabBarIndicatorHeight);
  root.style.setProperty('--v2-agent-button-size', v2.layout.agentButtonSize);
  root.style.setProperty('--v2-agent-button-offset', v2.layout.agentButtonOffset);
  root.style.setProperty('--v2-fab-size', v2.layout.fabSize);
  root.style.setProperty('--v2-fab-right', v2.layout.fabRight);
  root.style.setProperty('--v2-fab-bottom', v2.layout.fabBottom);
  root.style.setProperty('--v2-appointment-card-radius', v2.layout.appointmentCardRadius);
  root.style.setProperty(
    '--v2-appointment-card-padding',
    `${v2.layout.appointmentCardPaddingY} ${v2.layout.appointmentCardPaddingX}`
  );
  root.style.setProperty(
    '--v2-appointment-card-header-gap',
    v2.layout.appointmentCardHeaderGap
  );
  root.style.setProperty(
    '--v2-appointment-card-summary-gap',
    v2.layout.appointmentCardSummaryGap
  );
  root.style.setProperty(
    '--v2-appointment-card-padding-min',
    `${v2.layout.appointmentCardPaddingMinY} ${v2.layout.appointmentCardPaddingMinX}`
  );
  root.style.setProperty(
    '--v2-appointment-card-padding-compact',
    `${v2.layout.appointmentCardPaddingCompactY} ${v2.layout.appointmentCardPaddingCompactX}`
  );
  root.style.setProperty(
    '--v2-appointment-card-timeline-compact-max-height',
    `${v2.layout.appointmentCardTimelineCompactMaxHeight}px`
  );
  root.style.setProperty(
    '--v2-appointment-card-timeline-minimal-max-height',
    `${v2.layout.appointmentCardTimelineMinimalMaxHeight}px`
  );
  root.style.setProperty(
    '--v2-schedule-feed-time-col-width',
    v2.layout.scheduleFeedTimeColWidth
  );
  root.style.setProperty('--v2-schedule-day-gutter-y', v2.layout.scheduleDayGutterY);
  root.style.setProperty('--v2-schedule-day-gutter-x', v2.layout.scheduleDayGutterX);
  root.style.setProperty(
    '--v2-schedule-summary-padding-y',
    v2.layout.scheduleSummaryPaddingY
  );
  root.style.setProperty(
    '--v2-schedule-summary-padding-x',
    v2.layout.scheduleSummaryPaddingX
  );
  root.style.setProperty(
    '--v2-schedule-summary-padding',
    `${v2.layout.scheduleSummaryPaddingY} ${v2.layout.scheduleSummaryPaddingX}`
  );
  root.style.setProperty(
    '--v2-schedule-feed-row-padding-y',
    v2.layout.scheduleFeedRowPaddingY
  );
  root.style.setProperty('--v2-free-slot-min-height', v2.layout.freeSlotMinHeight);
  root.style.setProperty('--v2-list-row-min-height', v2.layout.listRowMinHeight);
  root.style.setProperty('--v2-list-row-avatar-size', v2.layout.listRowAvatarSize);
  root.style.setProperty(
    '--v2-client-detail-history-empty-min-height',
    v2.layout.clientDetailHistoryEmptyMinHeight
  );
  root.style.setProperty(
    '--v2-client-detail-history-time-min-width',
    v2.layout.clientDetailHistoryTimeMinWidth
  );
  root.style.setProperty('--v2-input-height', v2.layout.inputHeight);
  root.style.setProperty('--v2-sheet-grabber-width', v2.layout.sheetGrabberWidth);
  root.style.setProperty('--v2-sheet-grabber-height', v2.layout.sheetGrabberHeight);
  root.style.setProperty('--v2-tap-target-min', v2.layout.tapTargetMin);
  root.style.setProperty('--v2-screen-padding', v2.layout.screenPadding);
  root.style.setProperty('--v2-fab-icon-size', v2.layout.fabIconSize);
  root.style.setProperty('--v2-agent-icon-size', v2.layout.agentIconSize);
  root.style.setProperty('--v2-form-field-gap', v2.layout.formFieldGap);
  root.style.setProperty('--v2-list-row-padding-x', v2.layout.listRowPaddingX);
  root.style.setProperty('--v2-list-row-padding-y', v2.layout.listRowPaddingY);

  Object.entries(v2.spacing).forEach(([key, value]) => {
    root.style.setProperty(`--v2-spacing-${key}`, value);
  });

  root.style.setProperty('--v2-font-family', v2.typography.fontFamily);
  root.style.setProperty(
    '--v2-font-variant-numeric-tabular',
    v2.typography.fontVariantNumericTabular
  );
  Object.entries(v2.typography.fontSize).forEach(([key, value]) => {
    root.style.setProperty(`--v2-fs-${v2CssKey(key)}`, value);
  });
  Object.entries(v2.typography.fontWeight).forEach(([key, value]) => {
    root.style.setProperty(`--v2-fw-${v2CssKey(key)}`, String(value));
  });
  Object.entries(v2.typography.letterSpacing).forEach(([key, value]) => {
    root.style.setProperty(`--v2-ls-${v2CssKey(key)}`, value);
  });
  Object.entries(v2.typography.lineHeight).forEach(([key, value]) => {
    root.style.setProperty(`--v2-lh-${v2CssKey(key)}`, value);
  });

  const appointmentStatusV2Colors: Record<string, string> = {
    scheduled: v2Colors.status.pending,
    completed: v2Colors.status.done,
    cancelled: v2Colors.status.cancelled,
    'no-show': v2Colors.status.noShow,
    moved: v2Colors.status.moved,
    neutral: v2Colors.status.neutral,
    break: v2Colors.status.neutral,
  };

  Object.entries(appointmentStatusV2Colors).forEach(([key, baseColor]) => {
    root.style.setProperty(`--v2-status-stripe-${key}`, baseColor);
    root.style.setProperty(`--v2-status-pill-bg-${key}`, v2StatusPillBg(baseColor));
    root.style.setProperty(`--v2-status-pill-text-${key}`, v2StatusPillText(baseColor));
    root.style.setProperty(
      `--v2-status-card-bg-${key}`,
      `color-mix(in srgb, ${baseColor} 10%, var(--v2-surface) 90%)`
    );
    root.style.setProperty(
      `--v2-status-card-border-${key}`,
      `color-mix(in srgb, ${baseColor} 52%, var(--v2-border) 48%)`
    );
  });
}
